import React, { useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, AlertTriangle, Car, BatteryCharging, Play, Loader2, RotateCcw, CarFront } from 'lucide-react';
import {
  VEHICLE_FIELD_SPECS, BATTERY_FIELD_SPECS, DEFAULT_VEHICLE, DEFAULT_BATTERY,
  DRIVE_CYCLE_OPTIONS, DEFAULT_DRIVE_CYCLE,
  EV_PRESETS, DEFAULT_PRESET_ID, findPreset, DRIVE_TECHNOLOGY_OPTIONS, CHEMISTRY_OPTIONS,
  SIMULATION_TIME_SPEC, SAMPLING_TIME_OPTIONS_S, DEFAULT_SAMPLING_TIME_S,
  validateField, validateCrossFields, toRawStrings,
} from '../../lib/simulationTools';
import { simulationApi } from '../../lib/simulationApi';
import ResultsSummaryPanel from './simulator/ResultsSummaryPanel';
import VehicleGraphsChart from './simulator/VehicleGraphsChart';
import GradientClimbChart from './simulator/GradientClimbChart';
import MotorRequirementPanel from './simulator/MotorRequirementPanel';
import MotorEnvelopeChart from './simulator/MotorEnvelopeChart';
import BatteryRequirementPanel from './simulator/BatteryRequirementPanel';
import BatteryDischargeChart from './simulator/BatteryDischargeChart';
import BatteryEnergyConsumptionChart from './simulator/BatteryEnergyConsumptionChart';

function validateGroup(raw, specs) {
  const errors = {};
  const values = {};
  for (const spec of specs) {
    const err = validateField(spec, raw[spec.key]);
    if (err) errors[spec.key] = err;
    else values[spec.key] = Number(raw[spec.key]);
  }
  return { errors, values };
}

const FieldInput = ({ spec, raw, error, onChange, testPrefix, placeholder }) => (
  <div className="space-y-1">
    <Label htmlFor={`${testPrefix}-${spec.key}`} className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 leading-tight">
      {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
    </Label>
    <Input
      id={`${testPrefix}-${spec.key}`}
      type="number"
      step="any"
      value={raw}
      onChange={(e) => onChange(spec.key, e.target.value)}
      placeholder={placeholder}
      className="font-mono h-8 text-sm"
      aria-invalid={!!error}
      aria-describedby={error ? `${testPrefix}-${spec.key}-error` : undefined}
      data-testid={`${testPrefix}-${spec.key}-input`}
    />
    {error && (
      <p id={`${testPrefix}-${spec.key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testPrefix}-${spec.key}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

const FieldGroup = ({ specs, rawValues, errors, onChange, testPrefix }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
    {specs.map((spec) => (
      <FieldInput key={spec.key} spec={spec} raw={rawValues[spec.key]} error={errors[spec.key]} onChange={onChange} testPrefix={testPrefix} />
    ))}
  </div>
);

const EvPerformanceRangeSimulator = () => {
  const [vehicleRaw, setVehicleRaw] = useState(() => toRawStrings(DEFAULT_VEHICLE));
  const [batteryRaw, setBatteryRaw] = useState(() => toRawStrings(DEFAULT_BATTERY));
  const [driveCycle, setDriveCycle] = useState(DEFAULT_DRIVE_CYCLE);
  // Blank by default: Submit works without it, and the backend then runs a range/endurance
  // simulation (loops the drive cycle until SOC reaches Minimum Battery SoC). After every
  // run this is set to the actual elapsed simulation time the backend reports back, so
  // re-submitting without touching it repeats the same duration.
  const [simulationTimeRaw, setSimulationTimeRaw] = useState('');
  const [samplingTimeS, setSamplingTimeS] = useState(DEFAULT_SAMPLING_TIME_S);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const preset = findPreset(presetId);

  // Loads every Vehicle + Battery field and the preset's certification drive cycle in one go.
  // Simulation Time is cleared so the run goes to Minimum SoC (the range the preset quotes).
  const loadPreset = (id) => {
    const p = findPreset(id);
    if (!p) return;
    setPresetId(id);
    setVehicleRaw(toRawStrings(p.vehicle));
    setBatteryRaw(toRawStrings(p.battery));
    setDriveCycle(p.drive_cycle);
    setSimulationTimeRaw('');
  };
  const presetModified = !!preset && JSON.stringify({ v: vehicleRaw, b: batteryRaw, c: driveCycle })
    !== JSON.stringify({ v: toRawStrings(preset.vehicle), b: toRawStrings(preset.battery), c: preset.drive_cycle });

  const setVehicleField = (key, value) => setVehicleRaw((prev) => ({ ...prev, [key]: value }));
  const setBatteryField = (key, value) => setBatteryRaw((prev) => ({ ...prev, [key]: value }));

  // Per-field checks first; cross-field rules (e.g. Max SoC vs Min SoC) only compare fields
  // that already passed their own check, and attach their message to the field to fix.
  const { vehicle, battery } = useMemo(() => {
    const v = validateGroup(vehicleRaw, VEHICLE_FIELD_SPECS);
    const b = validateGroup(batteryRaw, BATTERY_FIELD_SPECS);
    // Select-driven inputs live in the same raw objects (so presets load in one setState)
    // but aren't numeric FIELD_SPECS — pass them through as-is.
    v.values.drive_technology = vehicleRaw.drive_technology;
    b.values.chemistry = batteryRaw.chemistry;
    const cross = validateCrossFields(v.values, b.values);
    for (const [key, message] of Object.entries(cross)) {
      if (key in DEFAULT_VEHICLE) v.errors[key] = message;
      else b.errors[key] = message;
    }
    return { vehicle: v, battery: b };
  }, [vehicleRaw, batteryRaw]);
  const simulationTimeError = useMemo(
    () => validateField(SIMULATION_TIME_SPEC, simulationTimeRaw), [simulationTimeRaw],
  );
  const hasErrors = Object.keys(vehicle.errors).length > 0 || Object.keys(battery.errors).length > 0 || !!simulationTimeError;

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);

  // "Stale" (any input changed since the last successful run) is derived by comparing the
  // current inputs against a snapshot taken right after that run — not a useEffect watching
  // raw state, because runSimulation itself writes the actual elapsed Simulation Time back
  // into simulationTimeRaw once the response arrives, and that echo must NOT immediately
  // re-mark the results it belongs to as stale.
  const lastSubmittedSnapshotRef = useRef(null);
  const stale = !!result && lastSubmittedSnapshotRef.current !== null
    && lastSubmittedSnapshotRef.current !== JSON.stringify({ vehicleRaw, batteryRaw, driveCycle, simulationTimeRaw, samplingTimeS });

  const runSimulation = async () => {
    setRunning(true);
    setRunError(null);
    try {
      const response = await simulationApi.runFull({
        vehicle: vehicle.values,
        battery: battery.values,
        drive_cycle: driveCycle,
        simulation_time_s: simulationTimeRaw === '' ? null : Number(simulationTimeRaw),
        sampling_time_s: samplingTimeS,
      });
      setResult(response);
      const actualSimulationTimeRaw = String(response.summary.actual_simulation_time_s);
      setSimulationTimeRaw(actualSimulationTimeRaw);
      lastSubmittedSnapshotRef.current = JSON.stringify({
        vehicleRaw, batteryRaw, driveCycle, simulationTimeRaw: actualSimulationTimeRaw, samplingTimeS,
      });
    } catch (e) {
      setRunError(e.message);
    }
    setRunning(false);
  };

  return (
    <div className="space-y-6">
      {/* 1. Inputs */}
      <Card className="technical-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
            <Car className="h-5 w-5 text-autoxium-blue" />
            EV Powertrain Simulator
          </CardTitle>
          <CardDescription className="text-xs">
            Fill in the vehicle and battery parameters, then submit to size the motor and
            battery pack and run the full performance/energy simulation.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-1">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 mb-2 space-y-2" data-testid="preset-panel">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="space-y-1 flex-1 min-w-0">
                <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1.5">
                  <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
                </Label>
                <Select value={presetId} onValueChange={loadPreset}>
                  <SelectTrigger className="h-8 text-sm" data-testid="preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EV_PRESETS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {presetModified && (
                <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={() => loadPreset(presetId)} data-testid="preset-reset-button">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                </Button>
              )}
            </div>
            {preset && (
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-mono text-foreground/80" data-testid="preset-reference">
                  Real vehicle: {preset.reference}
                  {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
                </p>
                <p data-testid="preset-note">{preset.note}</p>
              </div>
            )}
          </div>
          <Accordion type="multiple" defaultValue={['vehicle', 'battery']}>
            <AccordionItem value="vehicle">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground py-2">
                <span className="flex items-center gap-2"><Car className="h-4 w-4" /> Vehicle Parameter</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-3 pt-0">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <SelectField
                    label="Motor & Inverter Technology" value={vehicleRaw.drive_technology}
                    onChange={(v) => setVehicleField('drive_technology', v)}
                    options={DRIVE_TECHNOLOGY_OPTIONS} testId="drive-technology-select"
                  />
                </div>
                <FieldGroup specs={VEHICLE_FIELD_SPECS} rawValues={vehicleRaw} errors={vehicle.errors} onChange={setVehicleField} testPrefix="vehicle" />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="battery">
              <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground py-2">
                <span className="flex items-center gap-2"><BatteryCharging className="h-4 w-4" /> Battery Parameter</span>
              </AccordionTrigger>
              <AccordionContent className="space-y-3 pb-3 pt-0">
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3">
                  <div className="space-y-1">
                    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">Drive Cycle</Label>
                    <Select value={driveCycle} onValueChange={setDriveCycle}>
                      <SelectTrigger className="h-8 text-sm" data-testid="drive-cycle-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DRIVE_CYCLE_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value} disabled={o.disabled}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">Sampling Time (s)</Label>
                    <Select value={String(samplingTimeS)} onValueChange={(v) => setSamplingTimeS(Number(v))}>
                      <SelectTrigger className="h-8 text-sm" data-testid="sampling-time-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SAMPLING_TIME_OPTIONS_S.map((s) => (
                          <SelectItem key={s} value={String(s)}>{s.toFixed(1)} s</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <SelectField
                    label="Cell Chemistry" value={batteryRaw.chemistry}
                    onChange={(v) => setBatteryField('chemistry', v)}
                    options={CHEMISTRY_OPTIONS} testId="chemistry-select"
                  />
                  <FieldInput
                    spec={SIMULATION_TIME_SPEC} raw={simulationTimeRaw} error={simulationTimeError}
                    onChange={(_key, value) => setSimulationTimeRaw(value)} testPrefix="battery"
                    placeholder="Blank = run to Min. SoC"
                  />
                </div>
                <FieldGroup specs={BATTERY_FIELD_SPECS} rawValues={batteryRaw} errors={battery.errors} onChange={setBatteryField} testPrefix="battery" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          {/* 2. Submit */}
          <div className="pt-3">
            <Button onClick={runSimulation} disabled={hasErrors || running} className="w-full" data-testid="run-simulation-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Submit &amp; Run Simulation
            </Button>
            {runError && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-3" data-testid="simulator-run-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {runError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* 3. Results */}
      {result && (
        <div className={`space-y-6 transition-opacity ${stale ? 'opacity-50 pointer-events-none' : ''}`} data-testid="simulator-results">
          {stale && (
            <Alert data-testid="simulator-stale-banner" className="pointer-events-auto">
              <RotateCcw className="h-4 w-4" />
              <AlertDescription>Inputs have changed — submit again to update these results.</AlertDescription>
            </Alert>
          )}

          {result.warnings?.length > 0 && (
            <Alert data-testid="simulator-warnings" className="border-amber-500/50">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription>
                <p className="font-semibold mb-1">The simulation ran, but check these points:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {result.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <ResultsSummaryPanel summary={result.summary} />
          <VehicleGraphsChart timeS={result.time_s} timeTraceSpeedKph={result.time_trace_speed_kph} baselineSweep={result.baseline_sweep} />
          <GradientClimbChart sweep010={result.gradient_sweep_0_10} sweep020={result.gradient_sweep_0_20} />
          <MotorRequirementPanel motorRequirement={result.motor_requirement} />
          <MotorEnvelopeChart curveWithMargin={result.motor_curve_with_margin} curveWithoutMargin={result.motor_curve_without_margin} baseSpeedRpm={result.motor_requirement.base_speed_rpm} />
          <BatteryRequirementPanel batteryRequirement={result.battery_requirement} />
          <BatteryDischargeChart batterySeries={result.battery_series} />
          <BatteryEnergyConsumptionChart batterySeries={result.battery_series} />
        </div>
      )}

      {!result && (
        <Card className="technical-card">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="simulator-empty-state">
            Fill in the inputs above and submit to see results and charts.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EvPerformanceRangeSimulator;
